import React, { useEffect, useState } from 'react';
import { Descriptions, Modal, Select } from 'antd';
import { StaffUser } from '../../../generated';
import { CloseOutlined } from '@ant-design/icons';

interface SaveAssignmentProps {
  caseStatus: string;
  staffInfo: StaffUser[];
  currentStaff: StaffUser | undefined;
  selectedStaff: StaffUser | undefined;
  onSave: (values: StaffUser | null) => void;
  handleStaffSelection: (value: StaffUser | null) => void;
}

export const SaveAssignment: React.FC<SaveAssignmentProps> = (props) => {
  const { staffInfo, selectedStaff, onSave } = props;

  const [tempSelected, setTempSelected] = useState<string | null>(null);
  const [confirmedAssignee, setConfirmedAssignee] = useState<StaffUser | null>(null);
  const [previousAssignee, setPreviousAssignee] = useState<StaffUser | null>(null);

  const [state, dispatch] = useState({
    isModalVisible: false,
    modalAction: ''
  });

  const currUserPermissions =
    props.currentStaff?.role?.permissions?.idCasePermissions ||
    props.currentStaff?.role?.permissions?.credCasePermissions ||
    props.currentStaff?.role?.permissions?.sendCasePermissions;

  useEffect(() => {
    if (selectedStaff) {
      setConfirmedAssignee(selectedStaff);
    } else {
      setConfirmedAssignee(null);
    }
  }, [selectedStaff]);

  const handleClearAssignee = () => {
    setPreviousAssignee(confirmedAssignee);
    setConfirmedAssignee(null);
    dispatch({ ...state, isModalVisible: true, modalAction: 'remove' });
  };

  const handleChange = (value: string) => {
    setTempSelected(value);
    dispatch({ ...state, isModalVisible: true, modalAction: 'assign' });
  };

  const handleModalOk = () => {
    if (state.modalAction === 'assign') {
      const selectedAssignee = staffInfo.find((staff) => staff.id === tempSelected) || null;
      setConfirmedAssignee(selectedAssignee);
      onSave(selectedAssignee);
    } else if (state.modalAction === 'remove') {
      setConfirmedAssignee(null);
      onSave(null);
    }
    setPreviousAssignee(null);
    dispatch({ ...state, isModalVisible: false });
    setTempSelected(null);
  };

  const handleModalCancel = () => {
    if (state.modalAction === 'remove') {
      setConfirmedAssignee(previousAssignee);
    }
    dispatch({ ...state, isModalVisible: false });
    setTempSelected(null);
  };

  const shouldRenderUnassignButton = () => {
    return (
      (currUserPermissions?.canUnassignCaseWorker || (currUserPermissions?.canUnassignSelf && props.currentStaff?.displayName === confirmedAssignee?.displayName)) &&
      !(currUserPermissions?.canUnassignCaseWorker && props.currentStaff?.displayName === confirmedAssignee?.displayName && !currUserPermissions?.canUnassignSelf) &&
      (props.caseStatus !== 'decisionRendered')
    );
  };

  return (
    <div>
      <Descriptions
        title={undefined}
        layout="vertical"
        size="small"
        column={1}
        className="w-[400px]"
        style={{
          marginTop: -20,
          padding: 24,
          minHeight: '100%'
        }}
      >
        <Descriptions.Item label="Assigned To">
          {props.caseStatus === 'decisionRendered' ? (
            confirmedAssignee ? (
              <span>{confirmedAssignee.displayName}</span>
            ) : (
              <span>N/A</span> // Show nothing underneath "Assigned To:" if no assignee
            )
          ) : confirmedAssignee ? (
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <span style={{ paddingRight: '24px' }}>{confirmedAssignee.displayName}</span>
              {shouldRenderUnassignButton() && (
                <CloseOutlined
                  style={{
                    color: 'red',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    cursor: 'pointer'
                  }}
                  onClick={handleClearAssignee}
                />
              )}
            </div>
          ) : currUserPermissions?.canAssignCaseWorker ? (
            <Select
              key={confirmedAssignee ? confirmedAssignee : 'no-assignee'}
              placeholder="Select a Member"
              style={{ width: '45%' }}
              value={tempSelected ?? selectedStaff?.id}
              onChange={handleChange}
            >
              {staffInfo
                .filter((staff) => !(staff.displayName === props.currentStaff?.displayName && !currUserPermissions?.canAssignSelf))
                .map((staff) => (
                  <Select.Option key={staff.id} value={staff.id}>
                    {staff.displayName}
                  </Select.Option>
                ))}
            </Select>
          ) : currUserPermissions?.canAssignSelf ? (
            <Select
              key={confirmedAssignee ? confirmedAssignee : 'no-assignee'}
              placeholder="Select a Member"
              style={{ width: '45%' }}
              value={tempSelected ?? selectedStaff?.id}
              onChange={handleChange}
            >
              {props.currentStaff && (
                <Select.Option key={props.currentStaff.id} value={props.currentStaff.id}>
                  {props.currentStaff.displayName}
                </Select.Option>
              )}
            </Select>
          ) : (
            <Select
              key={confirmedAssignee ? confirmedAssignee : 'no-assignee'}
              placeholder="Select a Member"
              style={{ width: '45%' }}
              value={tempSelected ?? selectedStaff?.id}
              onChange={handleChange}
              disabled
            ></Select>
          )}
        </Descriptions.Item>
      </Descriptions>
      <Modal
        title={state.modalAction === 'assign' ? 'Confirm Assignment' : 'Remove Assignee'}
        open={state.isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText="Yes"
        cancelText="No"
      >
        <p>{state.modalAction === 'assign' ? 'Are you sure you want to select this Assignee?' : 'Are you sure you want to remove this Assignee?'}</p>
      </Modal>
    </div>
  );
};
